import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../auth";
import { countUntaggedPhotos } from "../db/photos";
import { listActiveTasks, completeTask } from "../db/tasks";
import { listPlants } from "../db/plants";
import { listLocations } from "../db/locations";
import { CameraCard } from "../components/CameraCard";
import { useCurrentGardenId } from "../lib/useCurrentGarden";
import type { TaskRow, PlantRow, LocationRow } from "@garden/types/db";
import "./Today.css";

const UNDO_MS = 5000;

interface PendingComplete {
  taskId: string;
  taskName: string;
  startedAt: number;
  timeoutId: ReturnType<typeof setTimeout>;
}

export function Today() {
  const [untagged, setUntagged] = useState<number | null>(null);
  const [tasks, setTasks] = useState<TaskRow[] | null>(null);
  const [plants, setPlants] = useState<PlantRow[]>([]);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Tasks the user has marked complete in the UI but the DB write is queued
  // for `UNDO_MS` to allow undo. Keyed by task id.
  const [pending, setPending] = useState<Map<string, PendingComplete>>(new Map());
  const pendingRef = useRef<Map<string, PendingComplete>>(new Map());
  pendingRef.current = pending;

  const gardenId = useCurrentGardenId();

  const refreshUntagged = async () => {
    if (!supabase || !gardenId) return;
    try {
      const n = await countUntaggedPhotos(supabase, gardenId);
      setUntagged(n);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const reload = async () => {
    if (!supabase || !gardenId) return;
    try {
      const [t, p, l] = await Promise.all([
        listActiveTasks(supabase, 7, gardenId),
        listPlants(supabase, gardenId),
        listLocations(supabase, gardenId),
      ]);
      setTasks(t);
      setPlants(p);
      setLocations(l);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void refreshUntagged();
    void reload();
  }, [gardenId]);

  // Flush any pending completions on unmount, so navigating away doesn't
  // strand the user with un-applied "completes."
  useEffect(() => {
    return () => {
      const current = pendingRef.current;
      for (const p of current.values()) {
        clearTimeout(p.timeoutId);
        // Best-effort: write but don't await. RLS ensures user_id checks.
        if (supabase) {
          void completeTask(supabase, { task_id: p.taskId }).catch(() => {});
        }
      }
    };
  }, []);

  const plantById = useMemo(() => {
    const m = new Map<string, PlantRow>();
    plants.forEach((p) => m.set(p.id, p));
    return m;
  }, [plants]);

  const locationById = useMemo(() => {
    const m = new Map<string, LocationRow>();
    locations.forEach((l) => m.set(l.id, l));
    return m;
  }, [locations]);

  // Sorted: overdue first (oldest first), then upcoming (soonest first).
  const sorted = useMemo(() => {
    if (!tasks) return [];
    const today = new Date().toISOString().slice(0, 10);
    return [...tasks].sort((a, b) => {
      const aOver = (a.due_date ?? "9999") < today ? 0 : 1;
      const bOver = (b.due_date ?? "9999") < today ? 0 : 1;
      if (aOver !== bOver) return aOver - bOver;
      return (a.due_date ?? "9999").localeCompare(b.due_date ?? "9999");
    });
  }, [tasks]);

  const onTickClick = (task: TaskRow) => {
    if (pending.has(task.id)) {
      // already pending — clicking again is a no-op
      return;
    }
    if (!supabase) return;
    const timeoutId = setTimeout(() => {
      // Time elapsed: actually write the completion.
      const current = pendingRef.current;
      const p = current.get(task.id);
      if (!p) return;
      void (async () => {
        try {
          if (supabase) {
            await completeTask(supabase, { task_id: task.id });
          }
          // Drop from pending and remove from active list.
          setPending((prev) => {
            const next = new Map(prev);
            next.delete(task.id);
            return next;
          });
          setTasks((prev) => (prev ?? []).filter((t) => t.id !== task.id));
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err));
          // Restore the task as pending=false so user can try again.
          setPending((prev) => {
            const next = new Map(prev);
            next.delete(task.id);
            return next;
          });
        }
      })();
    }, UNDO_MS);

    setPending((prev) => {
      const next = new Map(prev);
      next.set(task.id, {
        taskId: task.id,
        taskName: task.name,
        startedAt: Date.now(),
        timeoutId,
      });
      return next;
    });
  };

  const onUndo = (taskId: string) => {
    const p = pending.get(taskId);
    if (!p) return;
    clearTimeout(p.timeoutId);
    setPending((prev) => {
      const next = new Map(prev);
      next.delete(taskId);
      return next;
    });
  };

  // Latest pending completion drives the undo toast.
  const latestPending = useMemo(() => {
    let latest: PendingComplete | null = null;
    for (const p of pending.values()) {
      if (!latest || p.startedAt > latest.startedAt) latest = p;
    }
    return latest;
  }, [pending]);

  const now = new Date();
  const weekday = now.toLocaleDateString(undefined, { weekday: "long" });
  const dayMonth = now.toLocaleDateString(undefined, { day: "numeric", month: "long" });

  return (
    <div className="Today">
      <header className="Today__head">
        <div className="Today__eyebrow">{weekday}</div>
        <div className="Today__date">
          {dayMonth}
          <em className="Today__dot">.</em>
        </div>
      </header>

      <div className="Today__cameraWrap">
        <CameraCard onUploaded={() => void refreshUntagged()} />
      </div>

      {untagged !== null && untagged > 0 && (
        <Link to="/tag" className="Today__untagged">
          <span className="Today__untaggedBadge">{untagged}</span>
          <span className="Today__untaggedText">
            <span className="Today__untaggedTitle">
              {untagged === 1 ? "1 untagged photo" : `${untagged} untagged photos`}
            </span>
            <span className="Today__untaggedSub">Tag to attach to a plant</span>
          </span>
          <span className="Today__untaggedArrow" aria-hidden="true">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M6 4l4 4-4 4" />
            </svg>
          </span>
        </Link>
      )}

      <section className="Today__sectionH">
        <div className="Today__sectionTitle">Active tasks</div>
        <div className="Today__sectionMeta">
          ±7 days{tasks ? ` · ${tasks.length} task${tasks.length === 1 ? "" : "s"}` : ""}
        </div>
      </section>

      {tasks === null ? (
        <p className="Today__loading">Loading…</p>
      ) : sorted.length === 0 ? (
        <div className="Today__empty">
          <p>No tasks scheduled.</p>
          <p className="Today__emptyHint">
            Add one from a plant or the <Link to="/tasks">Tasks page</Link>.
          </p>
        </div>
      ) : (
        <ul className="Today__tasks">
          {sorted.map((t) => (
            <ActiveTaskRow
              key={t.id}
              task={t}
              plant={t.plant_id ? plantById.get(t.plant_id) ?? null : null}
              location={(() => {
                const id =
                  t.location_id ??
                  (t.plant_id ? plantById.get(t.plant_id)?.location_id ?? null : null);
                return id ? locationById.get(id) ?? null : null;
              })()}
              isPending={pending.has(t.id)}
              onTick={() => onTickClick(t)}
            />
          ))}
        </ul>
      )}

      {error && <div className="Today__error">{error}</div>}

      {latestPending && (
        <UndoToast
          key={latestPending.taskId + latestPending.startedAt}
          name={latestPending.taskName}
          onUndo={() => onUndo(latestPending.taskId)}
        />
      )}
    </div>
  );
}

function ActiveTaskRow({
  task,
  plant,
  location,
  isPending,
  onTick,
}: {
  task: TaskRow;
  plant: PlantRow | null;
  location: LocationRow | null;
  isPending: boolean;
  onTick: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const overdue = task.due_date && task.due_date < today;
  const meta: string[] = [];
  if (task.due_date) {
    if (overdue) {
      meta.push(`${diffDays(task.due_date, today)} overdue`);
    } else if (task.due_date === today) {
      meta.push("Today");
    } else {
      meta.push(`In ${diffDays(today, task.due_date)}`);
    }
  } else {
    meta.push("No due date");
  }
  if (location?.name) meta.push(location.name);
  else if (plant?.common_name) meta.push(plant.common_name);

  return (
    <li className={`Today__task ${isPending ? "Today__task--pending" : ""}`}>
      <button
        type="button"
        className="Today__taskTick"
        onClick={onTick}
        aria-label={`Mark "${task.name}" complete`}
      >
        {isPending && <span className="Today__taskTickInner" aria-hidden="true">✓</span>}
      </button>
      <div className="Today__taskBody">
        <div className="Today__taskName">
          {plant ? (
            <Link to={`/plants/${plant.id}`} className="Today__taskLink">
              {task.name}
            </Link>
          ) : (
            task.name
          )}
        </div>
        <div className="Today__taskMeta">
          <span className={overdue ? "Today__taskMetaOverdue" : ""}>{meta[0]}</span>
          {meta[1] && <span> · {meta[1]}</span>}
        </div>
      </div>
    </li>
  );
}

function UndoToast({ name, onUndo }: { name: string; onUndo: () => void }) {
  // Triggers a CSS animation that drains the bar over UNDO_MS.
  return (
    <div className="Today__undo" role="status" aria-live="polite">
      <div className="Today__undoText">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <path d="M3 8a5 5 0 1 1 5 5" />
          <path d="M5 6L3 8l2 2" />
        </svg>
        <span>{name} · marked complete</span>
      </div>
      <button type="button" className="Today__undoBtn" onClick={onUndo}>
        Undo
      </button>
      <div
        className="Today__undoBar"
        style={{ animationDuration: `${UNDO_MS}ms` }}
        aria-hidden="true"
      />
    </div>
  );
}

function diffDays(fromIso: string, toIso: string): string {
  const ms =
    new Date(toIso + "T00:00:00").getTime() -
    new Date(fromIso + "T00:00:00").getTime();
  const days = Math.round(ms / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "1 day";
  return `${Math.abs(days)} days`;
}
